import axios, { InternalAxiosRequestConfig } from 'axios';
import { ShippingConfig, ShippingMethod } from '../types';
import { isShopClosedPayload, SHOP_CLOSED_EVENT } from '../components/shop/shopGateRules';

declare module 'axios' {
  interface AxiosRequestConfig {
    /** 401-nél se token-frissítés, se átirányítás (pl. kijelentkezés: a hibája nem számít). */
    skipAuthRefresh?: boolean;
    /** Belső jelző: ezt a kérést már egyszer megismételtük friss tokennel. */
    _retry?: boolean;
  }
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const ACCESS_KEY = 'token';
const REFRESH_KEY = 'refresh_token';

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem(ACCESS_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// --- Token-refresh flow (A29) ---------------------------------------------
// Lejáró access-tokennél NEM dobjuk ki azonnal a felhasználót: a tárolt
// refresh-tokennel új access-t kérünk és megismételjük az eredeti kérést.
// Single-flight: párhuzamos 401-eknél egyetlen frissítés fut, a többi vár rá.
let isRefreshing = false;
let refreshWaiters: ((token: string | null) => void)[] = [];
const onRefreshed = (token: string | null) => {
  refreshWaiters.forEach((cb) => cb(token));
  refreshWaiters = [];
};

/** Munkamenet törlése. Bejelentkezésre csak akkor irányít, ha ennek a lapnak volt
 *  munkamenete (access token) — nyilvános oldalon, munkamenet nélkül csendben töröl. */
const clearSessionAndRedirect = (hadSession: boolean) => {
  sessionStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  const { pathname, search } = window.location;
  const onAuthPage = pathname.startsWith('/bejelentkezes');
  if (hadSession && !onAuthPage) {
    // Bejelentkezés után ide térünk vissza (a LoginPage a ?next=-et ellenőrzi).
    const next = encodeURIComponent(pathname + (search || ''));
    window.location.href = `/bejelentkezes?next=${next}`;
  }
};

// --- Refresh több lapon ------------------------------------------------------
// Rotációnál (ROTATE_REFRESH_TOKENS + BLACKLIST_AFTER_ROTATION) két lap egyidejű
// frissítése egymás tokenjét tenné tiltólistára. Ezért:
//  - a frissítést lapok között sorba állítjuk (Web Locks), és a zárban ELŐSZÖR újraolvassuk
//    a tárolt tokent — ha közben egy másik lap rotált, már azzal frissítünk;
//  - zár nélküli böngészőben (tartalék): 401/400 után ~300 ms-ot várunk, és ha közben
//    megváltozott a tárolt token, azzal próbálunk újra;
//  - SOHA nem töröljük azt a tokent, amit nem mi küldtünk el, és csak a szerver
//    elutasítására (401/400) törlünk — hálózati hiba / időtúllépés / 5xx nem léptet ki.
const REFRESH_LOCK = 'gt-auth-refresh';
const REFRESH_TIMEOUT_MS = 15_000;
export const REFRESH_RECHECK_DELAY_MS = 300;

interface LockManagerLike {
  request<T>(name: string, callback: () => Promise<T>): Promise<T>;
}

const getLocks = (): LockManagerLike | undefined =>
  (typeof navigator !== 'undefined' ? (navigator as unknown as { locks?: LockManagerLike }).locks : undefined);

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

type RefreshOutcome = { ok: true; access: string } | { ok: false; error: unknown };

const statusOf = (e: unknown) => (e as { response?: { status?: number } } | null)?.response?.status;

/** Frissítés (a zárban vagy tartalékként anélkül). Sikernél a tokeneket el is tárolja. */
async function refreshWithStoredToken(hadAccess: boolean, locked: boolean): Promise<RefreshOutcome> {
  let nextToken = localStorage.getItem(REFRESH_KEY); // újraolvasás: más lap közben rotálhatott
  let usedToken: string | null = null; // az utoljára TÉNYLEGESEN elküldött token
  let lastError: unknown = new Error('Nincs refresh-token.');
  let waited = false;
  for (let attempt = 0; attempt < 2 && nextToken; attempt += 1) {
    usedToken = nextToken;
    nextToken = null;
    try {
      // Bare axios (nem az `api` példány) — így a refresh nem esik vissza ide.
      const resp = await axios.post(
        `${API_BASE_URL}/auth/refresh/`,
        { refresh: usedToken },
        { timeout: REFRESH_TIMEOUT_MS },
      );
      const access: unknown = resp.data?.access;
      const refresh: unknown = resp.data?.refresh;
      if (typeof access !== 'string' || !access) {
        // Hibás 200-as válasz: nem tárolunk semmit, és nem is léptetünk ki.
        return { ok: false, error: new Error('Érvénytelen token-frissítési válasz.') };
      }
      sessionStorage.setItem(ACCESS_KEY, access);
      if (typeof refresh === 'string' && refresh) localStorage.setItem(REFRESH_KEY, refresh); // rotációnál
      return { ok: true, access };
    } catch (e) {
      lastError = e;
      const status = statusOf(e);
      let stored = localStorage.getItem(REFRESH_KEY);
      if ((status === 401 || status === 400) && stored === usedToken && !locked && !waited) {
        // Tartalék (nincs zár): egy másik lap épp most írhatja az új tokent.
        waited = true;
        await sleep(REFRESH_RECHECK_DELAY_MS);
        stored = localStorage.getItem(REFRESH_KEY);
      }
      // Egy másik lap közben rotálta: újra a frissel. Különben valódi hiba (vagy kijelentkeztek).
      if (stored && stored !== usedToken) nextToken = stored;
    }
  }
  const status = statusOf(lastError);
  if ((status === 401 || status === 400) && usedToken && localStorage.getItem(REFRESH_KEY) === usedToken) {
    clearSessionAndRedirect(hadAccess);
  }
  return { ok: false, error: lastError };
}

const refreshAcrossTabs = (hadAccess: boolean): Promise<RefreshOutcome> => {
  const locks = getLocks();
  if (!locks) return refreshWithStoredToken(hadAccess, false);
  return locks.request(REFRESH_LOCK, () => refreshWithStoredToken(hadAccess, true));
};

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original: InternalAxiosRequestConfig | undefined = error.config;
    const status = error.response?.status;
    // Zárt bolt mód: a nyitva hagyott lap is átváltson a „Hamarosan” oldalra.
    if (status === 503 && isShopClosedPayload(error.response?.data)) {
      window.dispatchEvent(new Event(SHOP_CLOSED_EVENT));
    }
    if (status !== 401 || !original || original.skipAuthRefresh || original._retry) {
      return Promise.reject(error);
    }

    const refreshToken = localStorage.getItem(REFRESH_KEY);
    const hadAccess = !!sessionStorage.getItem(ACCESS_KEY);

    // Nincs refresh-token: vendég (auth-hoz kötött végpont, pl. /cart/) → csak
    // adjuk tovább a 401-et; lejárt régi munkamenet (volt access) → login.
    if (!refreshToken) {
      if (hadAccess) clearSessionAndRedirect(true);
      return Promise.reject(error);
    }

    original._retry = true;

    // Ha épp fut egy frissítés, várjuk meg az eredményét.
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshWaiters.push((token) => {
          if (token) {
            original.headers.Authorization = `Bearer ${token}`;
            resolve(api(original));
          } else {
            reject(error);
          }
        });
      });
    }

    isRefreshing = true;
    let outcome: RefreshOutcome;
    try {
      outcome = await refreshAcrossTabs(hadAccess);
    } catch (e) {
      outcome = { ok: false, error: e }; // pl. a zár-API vagy a tároló hibája
    }
    isRefreshing = false;
    if (!outcome.ok) {
      onRefreshed(null); // a várakozók a saját eredeti hibájukkal utasítódnak el
      return Promise.reject(outcome.error);
    }
    onRefreshed(outcome.access);
    // Az eredeti kérés megismétlése az új tokennel (ennek a hibája már a hívóé).
    original.headers.Authorization = `Bearer ${outcome.access}`;
    return api(original);
  }
);

// Shipping Methods
// `orderValue` = a kosár termékösszege a csoport- és kupon-kedvezmény UTÁN — a szerver ehhez számolja a
// tényleges díjat (ingyenes szállítás a küszöb felett).
export const getShippingMethods = (orderValue?: number) => {
  const params = orderValue !== undefined ? { order_value: Math.max(0, Math.round(orderValue)) } : {};
  return api.get<ShippingMethod[]>('/shipping-methods/', { params });
};

export const getShippingConfig = () => api.get<ShippingConfig>('/shipping/config/');

// Payment Methods
export const createPaymentIntent = (orderId: number) => {
  return api.post('/payments/create_payment_intent/', { order_id: orderId });
};

export const confirmPayment = (paymentId: number) => {
  return api.post(`/payments/${paymentId}/confirm_payment/`);
};

// Coupon Methods
// A kedvezményt a szerver a vásárló tényleges kosarából számolja (egész forint).
export const validateCoupon = (code: string) => {
  return api.post('/coupons/validate/', { code });
};

export default api;
